import type { PrepareIssueItem } from "@/app/artifacts/accounting/AccountingPreparePanel";
import { createMarketApiFetch } from "@/lib/marketApiFetch";
import {
  feedTabSlug,
  interpretFeedHttp,
  isJunkUnmatchedFeedItem,
} from "@/lib/financialWork/feedClient";
import {
  FINANCIAL_WORK_NAV_PARAM,
  FINANCIAL_WORK_OBJECT_PARAM,
  objectIdParamForKind,
} from "@/lib/financialWork/nav";
import { translateReasonSubtitle } from "@/lib/financialWork/reasonLabelsTh";
import type { FinancialWorkFeedItem } from "@/lib/financialWork/types";

const marketApiFetch = createMarketApiFetch();
const FEED_PAGE_SIZE = 100;

export function feedItemPeriodKey(item: FinancialWorkFeedItem): string | null {
  const raw = item.updatedAt?.trim() || item.createdAt?.trim();
  if (!raw) return null;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Bangkok",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(parsed);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  if (!year || !month) return null;
  return `${year}-${month}`;
}

/** Match งานเงิน queue date (updatedAt → createdAt) to accounting period YYYY-MM. */
export function feedItemMatchesPeriod(
  item: FinancialWorkFeedItem,
  period: string,
): boolean {
  const target = period.trim();
  if (!target) return true;
  const key = feedItemPeriodKey(item);
  return key === target;
}

export function filterFeedItemsForPeriod(
  items: FinancialWorkFeedItem[],
  period: string | null | undefined,
): FinancialWorkFeedItem[] {
  const target = period?.trim();
  if (!target) return items;
  return items.filter((item) => feedItemMatchesPeriod(item, target));
}

export function countFeedItemsByBucket(
  items: FinancialWorkFeedItem[],
): { needs_action: number; waiting: number } {
  let needs_action = 0;
  let waiting = 0;
  for (const item of items) {
    if (item.bucket === "needs_action") needs_action += 1;
    else if (item.bucket === "waiting") waiting += 1;
  }
  return { needs_action, waiting };
}

export type PrepareDrawerFeedSnapshot = {
  items: FinancialWorkFeedItem[];
  counts: { needs_action: number; waiting: number };
};

export function formatFeedAmountMinor(amountMinor: number): string {
  const major = amountMinor / 100;
  return `฿${major.toLocaleString("th-TH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

export function buildPrepareWorkItemHref(args: {
  item: FinancialWorkFeedItem;
  serviceId?: string | null;
  workspaceId: string;
  returnTo?: string;
}): string {
  const params = new URLSearchParams();
  if (args.workspaceId.trim()) {
    params.set("workspaceId", args.workspaceId.trim());
  }
  if (args.serviceId?.trim()) {
    params.set("serviceId", args.serviceId.trim());
  }
  params.set(FINANCIAL_WORK_NAV_PARAM, "work");
  const idKey = objectIdParamForKind(args.item.objectRef.kind);
  params.set(FINANCIAL_WORK_OBJECT_PARAM, idKey);
  params.set(idKey, args.item.objectRef.id);
  const safeReturn = args.returnTo?.trim();
  if (safeReturn) params.set("returnTo", safeReturn);
  return `/?${params.toString()}`;
}

export function feedItemToPrepareIssue(
  item: FinancialWorkFeedItem,
  href: string,
): PrepareIssueItem {
  const reasonSubtitle = translateReasonSubtitle(item.subtitle);
  return {
    id: `${item.objectRef.kind}:${item.objectRef.id}`,
    title: item.title,
    subtitle: reasonSubtitle || item.sourceLabel?.trim() || undefined,
    amountLabel: formatFeedAmountMinor(item.amountMinor),
    href,
  };
}

async function fetchFeedTab(args: {
  workspaceId: string;
  ownerUserId?: string | null;
  actorId: string;
  bucket: "needs_action" | "waiting";
  limit: number;
  page: number;
}) {
  const qs = new URLSearchParams({
    workspace_id: args.workspaceId,
    limit: String(args.limit),
    page: String(args.page),
    actor_id: args.actorId,
  });
  if (args.ownerUserId?.trim()) {
    qs.set("owner_user_id", args.ownerUserId.trim());
  }
  const res = await marketApiFetch(
    `/api/v1/financial-work/feed/${feedTabSlug(args.bucket)}?${qs.toString()}`,
    { cache: "no-store", headers: { Accept: "application/json" } },
  );
  const body = await res.json().catch(() => ({}));
  return interpretFeedHttp(res.status, body);
}

async function fetchAllFeedTabItems(args: {
  workspaceId: string;
  ownerUserId?: string | null;
  actorId: string;
  bucket: "needs_action" | "waiting";
}): Promise<FinancialWorkFeedItem[]> {
  const items: FinancialWorkFeedItem[] = [];
  let page = 0;
  while (page < 50) {
    const outcome = await fetchFeedTab({
      ...args,
      limit: FEED_PAGE_SIZE,
      page,
    });
    if (outcome.kind !== "ok") break;
    const batch = (outcome.feed.items ?? []).filter(
      (item) => !isJunkUnmatchedFeedItem(item),
    );
    items.push(...batch);
    const total = outcome.feed.total ?? batch.length;
    if (items.length >= total || batch.length < FEED_PAGE_SIZE) break;
    page += 1;
  }
  return items;
}

/** Same feed tabs as งานเงิน — paginated needs-action + waiting with auth + owner scope. */
export async function fetchPrepareDrawerWorkFeed(args: {
  workspaceId: string;
  ownerUserId?: string | null;
  actorId: string;
}): Promise<PrepareDrawerFeedSnapshot> {
  const empty: PrepareDrawerFeedSnapshot = {
    items: [],
    counts: { needs_action: 0, waiting: 0 },
  };
  const workspaceId = args.workspaceId.trim();
  const actorId = args.actorId.trim();
  if (!workspaceId || !actorId) return empty;

  const [needsItems, waitingItems] = await Promise.all([
    fetchAllFeedTabItems({
      workspaceId,
      ownerUserId: args.ownerUserId?.trim() || undefined,
      actorId,
      bucket: "needs_action",
    }),
    fetchAllFeedTabItems({
      workspaceId,
      ownerUserId: args.ownerUserId?.trim() || undefined,
      actorId,
      bucket: "waiting",
    }),
  ]);

  const byKey = new Map<string, FinancialWorkFeedItem>();
  for (const item of [...needsItems, ...waitingItems]) {
    byKey.set(`${item.objectRef.kind}:${item.objectRef.id}`, item);
  }
  const items = [...byKey.values()];

  return {
    items,
    counts: countFeedItemsByBucket(items),
  };
}
